import { apiClient } from './client';

export interface User {
  id: string;
  account_id: number;
  email: string;
  full_name: string;
  role: 'user' | 'admin';
  country: string;
  base_principal_cents: number;
  base_principal_initialized_at: string | null;
  contributed_amount_cents: number;
  min_required_contribution_cents: number;
  profit_week_cents: number;
  withdrawable_cents: number;
  reinvest_percent: number;
  one_time_withdrawable_amount_cents?: number; // S45
  one_time_withdrawable_updated_at?: string | null; // S45
  locale?: string;
  email_notifications_enabled: boolean;
  notify_profit_allocation: boolean;
  notify_weekly_settlement: boolean;
  notify_withdrawal_status: boolean;
  notify_admin_updates: boolean;
  eligible_for_earnings: boolean;
  eligibility_status: string;
  contribution_shortfall_cents: number;
  tenure_years?: number;
  tenure_description?: string;
  company_cut_percent?: number;
  participation_status?: 'active' | 'pending_close' | 'exited'; // S41
}

export interface UserListResponse {
  users: User[];
}

export interface UserResponse {
  user: User;
}

export const userEndpoints = {
  // Get current user
  me: () => apiClient.get<UserResponse>('/api/me'),

  // Update current user's reinvestment percentage
  updateReinvestPercent: (percent: number) =>
    apiClient.patch<UserResponse>('/api/me/reinvest_percent', { reinvest_percent: percent }),

  // Update current user's locale/language preference
  updateLocale: (locale: string) =>
    apiClient.patch<UserResponse>('/api/me/locale', { locale }),

  // Update email notification preferences
  updateEmailNotifications: (preferences: {
    email_notifications_enabled?: boolean;
    notify_profit_allocation?: boolean;
    notify_weekly_settlement?: boolean;
    notify_withdrawal_status?: boolean;
    notify_admin_updates?: boolean;
  }) =>
    apiClient.patch<UserResponse>('/api/me/email_notifications', preferences),

  // Update current user's profile (name and email)
  updateProfile: (fullName: string, email: string) =>
    apiClient.patch<UserResponse>('/api/me/profile', { full_name: fullName, email }),

  // Change password
  changePassword: (currentPassword: string, newPassword: string, newPasswordConfirmation: string) =>
    apiClient.post<{ message: string }>('/api/me/change_password', {
      current_password: currentPassword,
      new_password: newPassword,
      new_password_confirmation: newPasswordConfirmation
    }),

  // S41: Account closure endpoints
  requestAccountClosure: () =>
    apiClient.post<{ message: string, status: string }>('/api/me/close_account/request', {}),

  cancelAccountClosure: () =>
    apiClient.delete<{ message: string, status: string }>('/api/me/close_account/request'),

  getClosureStatus: () =>
    apiClient.get<{ status: string, pending_request: boolean, closure_request?: any }>('/api/me/close_account/status'),

  // S45: One-time withdrawable allocation endpoints
  setOneTimeWithdrawable: (amountCents: number) =>
    apiClient.patch<UserResponse>('/api/me/one_time_withdrawable', { amount_cents: amountCents }),

  clearOneTimeWithdrawable: () =>
    apiClient.delete<UserResponse>('/api/me/one_time_withdrawable'),

  // Admin endpoints
  admin: {
    // List all users
    list: async () => {
      const response = await apiClient.get<UserListResponse>('/api/admin/users');
      return response.users; // Extract users array from wrapper object
    },

    // Get specific user
    get: async (userId: string) => {
      const response = await apiClient.get<UserResponse>(`/api/admin/users/${userId}`);
      return response.user; // Extract user from wrapper object
    },

    // Update user
    update: async (userId: string, data: Partial<User>) => {
      const response = await apiClient.patch<UserResponse>(`/api/admin/users/${userId}`, data);
      return response.user; // Extract user from wrapper object
    },

    // Delete user (only if 0% ownership)
    delete: async (userId: string) => {
      return await apiClient.delete(`/api/admin/users/${userId}`);
    },
  },
};
